import { useLocale } from '@solinkcorp/locale';
import { Button } from '@solinkcorp/solink-ui/components/Button';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@solinkcorp/solink-ui/components/DropdownMenu';

import { ChevronDown, Copy, EllipsisVertical, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useControllerState } from 'solox';
import { toast } from 'sonner';
import { getLayoutAdoptionMetrics } from '../../../analytics/sdmAdoptionMetrics';
import { SDM_EVENTS } from '../../../constants';
import { useSDMAnalytics } from '../../../contexts/AnalyticsContext';
import { useSDMClient } from '../../../contexts/CallhomeClientContext';
import { type Controller } from '../../../Controller';
import {
  useCustomerDefaultSDMLayout,
  useDeleteServiceDisplayLayout,
} from '../../../hooks/useServiceDisplayLayouts';
import LayoutDeleteConfirmationDialog from './LayoutDeleteConfirmationDialog';
import * as tw from './LayoutSelectorStyles';

export const LayoutSelector = (props: { controller: Controller; canEdit: boolean }) => {
  const { controller, canEdit } = props;
  const locale = useLocale();
  const analytics = useSDMAnalytics();
  const deleteLayout = useDeleteServiceDisplayLayout();
  const { data: defaultSdmLayoutId, customerId, mutate } = useCustomerDefaultSDMLayout();
  const callHomeClient = useSDMClient();

  const [open, setOpen] = useState(false);
  const [deleteConfirmLayout, setDeleteConfirmLayout] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const layouts = useControllerState(controller.state, (state) => state.layouts);
  const selectedLayout = useControllerState(controller.state, (state) => state.selectedLayout);

  const handleLayoutSelect = (layoutId: string) => {
    controller.selectLayout(layoutId);
    setOpen(false);
  };

  const handleDefaultSelection = async (layoutId: string) => {
    try {
      const isNewDefaultLayout = defaultSdmLayoutId !== layoutId;
      await callHomeClient.updateCustomerDefaultSdmLayout(
        customerId,
        isNewDefaultLayout ? layoutId : null,
      );
      await mutate();
    } catch (error) {
      console.error('Failed to update default layout:', error);
      toast.error('Failed to update default layout. Please try again.', {
        position: 'bottom-right',
      });
    }
  };

  const handleDelete = async (layoutId: string) => {
    const wasSelected = selectedLayout?.id === layoutId;
    const layoutToDelete = layouts.find((l) => l.id === layoutId);
    try {
      await deleteLayout(layoutId).then(() => {
        analytics.trackSDMEvent(SDM_EVENTS.DELETE_LAYOUT, {
          description: 'Deleted a service display monitor layout',
          ...(layoutToDelete
            ? getLayoutAdoptionMetrics(layoutToDelete)
            : { layoutId: layoutId as string }),
          wasDefaultLayout: defaultSdmLayoutId === layoutId,
          remainingLayoutCount: Math.max(0, layouts.length - 1),
        });
        if (wasSelected) {
          const remainingLayouts = layouts.filter((l) => l.id !== layoutId);
          controller.selectLayout(remainingLayouts[0]?.id);
        }
      });
    } catch (error) {
      console.error('Failed to delete layout:', error);
      toast.error(locale.serviceDisplayMonitor.failedToDeleteLayout, {
        position: 'bottom-right',
      });
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <div className={tw.dropDownMenuTriggerStyles} data-testid="layout-selector-trigger">
          {selectedLayout?.name ? (
            <div className="inline-flex w-full items-center justify-between">
              <div className={tw.layoutLabelContainerStyles}>
                <span className={`text-text-secondary ${tw.layoutLabelStyles}`}>
                  {locale.serviceDisplayMonitor.layout}
                </span>
                <span className={`text-text-primary ${tw.layoutNameStyles}`}>
                  {selectedLayout?.name}
                </span>
              </div>
              <ChevronDown
                className={`size-4 text-text-primary transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </div>
          ) : (
            locale.serviceDisplayMonitor.createANewLayout
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-full overflow-visible border-border-muted p-0 shadow-[0_4px_12px_#00000026]"
        align="start"
        data-testid="layout-selector-content"
      >
        <DropdownMenuGroup>
          {layouts?.map((item, index) => {
            const isSelected = item.id === selectedLayout?.id;
            return (
              <DropdownMenuSub key={item.id}>
                <div
                  className={`${tw.baseLayoutStyles} ${isSelected ? tw.sdmSelectedItemStyles : ''}`}
                  onClick={() => handleLayoutSelect(item.id)}
                >
                  <span
                    data-testid={`layout-menu-item-${index}`}
                    className={`flex-1 pl-4 font-normal ${tw.ellipsisTextStyle} ${isSelected ? 'text-text-inverse' : 'text-text-primary'}`}
                  >
                    {item.name}
                  </span>
                  {defaultSdmLayoutId === item.id && (
                    <span
                      className={`mx-1 ${tw.layoutLabelStyles} ${isSelected ? 'text-interaction-primary-content-default' : 'text-text-primary'}`}
                    >
                      {locale.serviceDisplayMonitor.defaultLabel}
                    </span>
                  )}
                  {canEdit && (
                    <DropdownMenuSubTrigger
                      className={`${tw.dropDownMenuSubTriggerStyles} ${isSelected ? 'text-text-inverse' : 'text-text-primary'}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EllipsisVertical className={tw.ellipsisVerticalStyles} />
                    </DropdownMenuSubTrigger>
                  )}
                </div>
                {canEdit && (
                  <DropdownMenuSubContent className={tw.menuItemActionStyles}>
                    <DropdownMenuItem
                      className={tw.actionTextStyles}
                      disabled
                      data-testid="dupe-action-btn"
                    >
                      <Copy className="size-3" />
                      {locale.serviceDisplayMonitor.duplicate}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className={tw.actionTextStyles}
                      onSelect={() => controller.displayLayoutDialog(true, item.id)}
                      data-testid="rename-action-btn"
                    >
                      <Pencil className="size-3" />
                      {locale.serviceDisplayMonitor.rename}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className={tw.actionTextStyles}
                      onSelect={() => {
                        handleDefaultSelection(item.id);
                      }}
                      data-testid="default-action-btn"
                    >
                      <Star
                        className="size-3"
                        fill={defaultSdmLayoutId === item.id ? '#FFD700' : 'none'}
                        color={defaultSdmLayoutId === item.id ? '#FFD700' : 'currentColor'}
                      />
                      {defaultSdmLayoutId === item.id
                        ? locale.serviceDisplayMonitor.default
                        : locale.serviceDisplayMonitor.makeDefault}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className={tw.actionTextStyles}
                      variant="destructive"
                      onSelect={() => setDeleteConfirmLayout({ id: item.id, name: item.name })}
                      data-testid="delete-action-btn"
                      disabled={layouts.length <= 1}
                    >
                      <Trash2 className="size-3 text-destructive" />
                      {locale.general.delete}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                )}
              </DropdownMenuSub>
            );
          })}
          {canEdit && (
            <DropdownMenuItem>
              <Button
                className={tw.dropDownMenuItemNewLayoutStyles}
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  analytics.trackSDMEvent(SDM_EVENTS.OPEN_TEMPLATE_PICKER, {
                    description: 'Opened the predefined layout template picker',
                  });
                  controller.showTemplatePicker();
                }}
                data-testid="new-layout-btn"
              >
                {locale.serviceDisplayMonitor.newLayout}
                <Plus className="margin-horizontal-auto size-4 bg-transparent text-text-accent" />
              </Button>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>

      <LayoutDeleteConfirmationDialog
        deleteConfirmLayout={deleteConfirmLayout}
        setDeleteConfirmLayout={setDeleteConfirmLayout}
        handleDelete={handleDelete}
      />
    </DropdownMenu>
  );
};
